import { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";

// ── Design tokens (matches Dashboard.jsx) ─────────────────────────────────────
const T = {
  bg:           "#0f1117",
  surface:      "#161b27",
  raised:       "#1a2035",
  border:       "rgba(255,255,255,0.07)",
  borderStrong: "rgba(255,255,255,0.13)",
  text:         "#e8eaf0",
  textMuted:    "#6b7280",
  textDim:      "#9ca3af",
  accent:       "#6366f1",
  accentLight:  "rgba(99,102,241,0.15)",
  success:      "#10b981",
  successLight: "rgba(16,185,129,0.12)",
  radius:       "14px",
  radiusSm:     "8px",
};

const LABELS = {
  w24h:     { label: "24 hours before",    desc: "Get reminded a full day ahead of the deadline." },
  w3h:      { label: "3 hours before",     desc: "A nudge a few hours out to help you finish up." },
  w1h:      { label: "1 hour before",      desc: "Last-chance alert before the deadline arrives." },
  dueToday: { label: "Due today",          desc: "Morning summary of everything due today." },
  overdue:  { label: "Overdue alerts",     desc: "Notify when an assignment passes its deadline." },
};

// ── Styled toggle ─────────────────────────────────────────────────────────────
function Toggle({ checked, onChange }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        width: 40,
        height: 22,
        borderRadius: 99,
        background: checked
          ? `linear-gradient(135deg, ${T.accent}, #818cf8)`
          : "rgba(255,255,255,0.1)",
        border: `1px solid ${checked ? "rgba(99,102,241,0.4)" : T.border}`,
        cursor: "pointer",
        transition: "background 0.2s, border-color 0.2s",
        flexShrink: 0,
        padding: 0,
        outline: "none",
        boxShadow: checked ? "0 0 0 3px rgba(99,102,241,0.15)" : "none",
      }}
    >
      <span
        style={{
          position: "absolute",
          left: checked ? 20 : 2,
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
          transition: "left 0.18s ease",
        }}
      />
    </button>
  );
}

// ── Section card wrapper ──────────────────────────────────────────────────────
function SettingsCard({ icon, title, description, children }) {
  return (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        overflow: "hidden",
      }}
    >
      {/* Card header */}
      <div
        style={{
          padding: "16px 20px",
          borderBottom: `1px solid ${T.border}`,
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            background: T.accentLight,
            border: `1px solid rgba(99,102,241,0.2)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, lineHeight: 1.3 }}>
            {title}
          </div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2, lineHeight: 1.4 }}>
            {description}
          </div>
        </div>
      </div>

      {/* Card body */}
      <div style={{ padding: "4px 0" }}>{children}</div>
    </div>
  );
}

// ── Row inside a card ─────────────────────────────────────────────────────────
function SettingsRow({ label, desc, children, last }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        padding: "13px 20px",
        borderBottom: last ? "none" : `1px solid ${T.border}`,
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{label}</div>
        {desc && (
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2, lineHeight: 1.4 }}>
            {desc}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

// ── Time input ────────────────────────────────────────────────────────────────
function TimeInput({ value, onChange }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type="time"
      value={value}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        background: T.raised,
        border: `1px solid ${focused ? "rgba(99,102,241,0.45)" : T.borderStrong}`,
        borderRadius: T.radiusSm,
        color: T.text,
        fontSize: 13,
        fontWeight: 500,
        padding: "7px 11px",
        outline: "none",
        fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
        width: 110,
        colorScheme: "dark",
        boxShadow: focused ? "0 0 0 3px rgba(99,102,241,0.12)" : "none",
        transition: "border-color 0.15s, box-shadow 0.15s",
      }}
    />
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function NotificationSettings() {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [s, setS] = useState({
    windows: { w24h: true, w3h: true, w1h: true, dueToday: true, overdue: true },
    quietHours: { start: "22:00", end: "07:00" },
  });

  useEffect(() => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;
    getDoc(doc(db, `users/${uid}/settings`, "general")).then((d) => {
      if (d.exists()) setS((prev) => ({ ...prev, ...d.data() }));
    });
  }, []);

  const save = async () => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;
    setSaving(true);
    await setDoc(doc(db, `users/${uid}/settings`, "general"), s, { merge: true });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    alert("Saved!");
  };

  const toggle = (key) =>
    setS((x) => ({ ...x, windows: { ...x.windows, [key]: !x.windows[key] } }));

  const windowKeys = Object.keys(s.windows);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        input[type="time"]::-webkit-calendar-picker-indicator { filter: invert(0.5); cursor: pointer; }
      `}</style>

      <div
        style={{
          minHeight: "100vh",
          background: T.bg,
          color: T.text,
          fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
          padding: "40px 24px 100px",
        }}
      >
        <div style={{ maxWidth: 600, margin: "0 auto" }}>

          {/* ── Page header ────────────────────────────────────────── */}
          <div style={{ marginBottom: 32 }}>
            <h1
              style={{
                fontSize: 24,
                fontWeight: 800,
                letterSpacing: "-0.5px",
                color: T.text,
                marginBottom: 6,
              }}
            >
              Notification Settings
            </h1>
            <p style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.5 }}>
              Control when and how DueQueue reminds you about upcoming deadlines.
            </p>
          </div>

          {/* ── Sections ───────────────────────────────────────────── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Reminders */}
            <SettingsCard
              icon="🔔"
              title="Reminders"
              description="Choose which deadline windows trigger a push notification."
            >
              {windowKeys.map((k, i) => (
                <SettingsRow
                  key={k}
                  label={LABELS[k]?.label || k}
                  desc={LABELS[k]?.desc}
                  last={i === windowKeys.length - 1}
                >
                  <Toggle checked={s.windows[k]} onChange={() => toggle(k)} />
                </SettingsRow>
              ))}
            </SettingsCard>

            {/* Quiet Hours */}
            <SettingsCard
              icon="🔕"
              title="Quiet Hours"
              description="No notifications will be sent during this window."
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                  padding: "16px 20px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: T.textMuted,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      marginBottom: 8,
                    }}
                  >
                    Start time
                  </div>
                  <TimeInput
                    value={s.quietHours.start}
                    onChange={(e) =>
                      setS((x) => ({
                        ...x,
                        quietHours: { ...x.quietHours, start: e.target.value },
                      }))
                    }
                  />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: T.textMuted,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      marginBottom: 8,
                    }}
                  >
                    End time
                  </div>
                  <TimeInput
                    value={s.quietHours.end}
                    onChange={(e) =>
                      setS((x) => ({
                        ...x,
                        quietHours: { ...x.quietHours, end: e.target.value },
                      }))
                    }
                  />
                </div>
              </div>
              <div
                style={{
                  margin: "0 20px 14px",
                  padding: "10px 13px",
                  background: "rgba(99,102,241,0.07)",
                  border: "1px solid rgba(99,102,241,0.18)",
                  borderRadius: T.radiusSm,
                  fontSize: 12,
                  color: "#a5b4fc",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                <span>🌙</span>
                Notifications will be silenced from{" "}
                <strong style={{ color: T.text }}>
                  {s.quietHours.start}
                </strong>{" "}
                to{" "}
                <strong style={{ color: T.text }}>
                  {s.quietHours.end}
                </strong>
              </div>
            </SettingsCard>

          </div>

          {/* ── Save button ─────────────────────────────────────────── */}
          <div
            style={{
              position: "sticky",
              bottom: 24,
              marginTop: 28,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <button
              onClick={save}
              disabled={saving}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "11px 24px",
                background: saving
                  ? "rgba(99,102,241,0.5)"
                  : `linear-gradient(135deg, ${T.accent}, #818cf8)`,
                border: "none",
                borderRadius: 10,
                color: "#fff",
                fontSize: 14,
                fontWeight: 700,
                cursor: saving ? "not-allowed" : "pointer",
                fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
                boxShadow: saving
                  ? "none"
                  : "0 4px 14px rgba(99,102,241,0.35)",
                transition: "all 0.15s",
                letterSpacing: "-0.1px",
              }}
            >
              {saving ? (
                <>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      border: "2px solid rgba(255,255,255,0.35)",
                      borderTopColor: "#fff",
                      borderRadius: "50%",
                      display: "inline-block",
                      animation: "dq-spin 0.7s linear infinite",
                    }}
                  />
                  Saving…
                </>
              ) : (
                "Save Settings"
              )}
            </button>
          </div>

        </div>
      </div>

      <style>{`
        @keyframes dq-spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
